/**
 * Provider Video Call Page
 * 
 * Full-screen video call experience for providers.
 * Includes a pre-join screen to preview camera/mic before joining.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VideoRoom, PreJoinScreen, EndCallStatusDialog } from '@/components/telehealth';
import type { PreJoinSettings } from '@/components/telehealth/PreJoinScreen';
import type { EndCallStatus } from '@/components/telehealth/EndCallStatusDialog';
import { telehealthService, TelehealthTokenResponse } from '@/services/telehealthService';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb';

const ProviderVideoCall: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { clinic } = useClinic();
  const { setLabel } = useBreadcrumbLabel();
  const [tokenData, setTokenData] = useState<TelehealthTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pre-join state - shows PreJoinScreen before entering the call
  const [showPreJoin, setShowPreJoin] = useState(true);
  const [deviceSettings, setDeviceSettings] = useState<PreJoinSettings>({
    audioEnabled: true,
    videoEnabled: true,
  });
  
  // Ref to prevent double fetch in React Strict Mode
  const fetchInitiatedRef = useRef(false);

  useEffect(() => {
    const patientName = tokenData?.appointment?.patientName?.trim();
    setLabel(patientName || 'Video Call');
    return () => setLabel(null);
  }, [setLabel, tokenData?.appointment?.patientName]);

  useEffect(() => {
    // Prevent duplicate fetch in React Strict Mode
    if (fetchInitiatedRef.current) {
      return;
    }
    fetchInitiatedRef.current = true;

    const fetchToken = async () => {
      if (!appointmentId) {
        setError('No appointment ID provided');
        setIsLoading(false);
        return;
      }

      try {
        console.log('Fetching provider token for appointment:', appointmentId);
        const data = await telehealthService.getProviderToken(appointmentId);
        console.log('Token received:', data);
        setTokenData(data);
      } catch (err: any) {
        console.error('Failed to get token:', err);
        const errorMessage = err?.response?.data?.message 
          || err?.response?.data?.error?.message 
          || err?.message 
          || 'Failed to join video call';
        console.error('Error details:', err?.response?.data);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchToken();
  }, [appointmentId]);

  // Show the end-call status dialog after the provider leaves the call
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  const handleLeave = async () => {
    // Show the status dialog so the provider can mark the outcome
    setShowStatusDialog(true);
  };

  // Called when the provider confirms a status in the dialog
  const handleStatusConfirm = async (status: EndCallStatus) => {
    if (appointmentId) {
      try {
        await telehealthService.endRoom(appointmentId, status);
      } catch (err) {
        console.error('Failed to end room:', err);
      }
    }
    navigate(-1);
  };

  const handleError = (error: Error) => {
    setError(error.message);
  };

  // Handle pre-join completion
  const handlePreJoinComplete = (settings: PreJoinSettings) => {
    setDeviceSettings(settings);
    setShowPreJoin(false);
  };

  // Handle pre-join cancel
  const handlePreJoinCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-180px)] min-h-[500px] w-full rounded-xl bg-[#0a0f14]">
        <Loader2 className="h-10 w-10 animate-spin text-brand-teal mb-4" />
        <p className="text-white text-base">Connecting to video call...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-180px)] min-h-[500px] w-full rounded-xl bg-[#0a0f14] p-4">
        <Card className="max-w-md w-full bg-[#141c24] border-red-500/20">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2 text-white">Unable to Join Call</h2>
            <p className="text-slate-400 text-sm mb-4">{error}</p>
            <p className="text-xs text-slate-500 mb-6">Appointment ID: {appointmentId}</p>
            <Button onClick={() => navigate(-1)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenData) {
    return null;
  }

  // Show pre-join screen first to preview camera/mic
  if (showPreJoin) {
    return (
      <div className="h-[calc(100vh-180px)] w-full min-h-[500px] rounded-xl overflow-auto">
        <PreJoinScreen
          onJoin={handlePreJoinComplete}
          onCancel={handlePreJoinCancel}
          displayName={tokenData.displayName}
          appointmentInfo={{
            patientName: tokenData.appointment.patientName,
            startTime: tokenData.appointment.startTime,
          }}
          isProvider={true}
        />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] w-full min-h-[500px] rounded-xl overflow-hidden">
      <VideoRoom
        roomUrl={tokenData.roomUrl}
        token={tokenData.token}
        displayName={tokenData.displayName}
        isProvider={true}
        appointmentInfo={{
          patientName: tokenData.appointment.patientName,
          startTime: tokenData.appointment.startTime,
          duration: tokenData.appointment.duration
        }}
        clinicId={clinic?.id}
        appointmentId={appointmentId}
        // Apply pre-join device settings
        startVideoOff={!deviceSettings.videoEnabled}
        startAudioOff={!deviceSettings.audioEnabled}
        onLeave={handleLeave}
        onError={handleError}
      />

      {/* Post-call status dialog */}
      <EndCallStatusDialog
        open={showStatusDialog}
        onConfirm={handleStatusConfirm}
        patientName={tokenData.appointment.patientName}
      />
    </div>
  );
};

export default ProviderVideoCall;
